import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Form, Card, Table, Button, Alert, Container } from 'react-bootstrap';
import { entityService, Entity } from '../../../services/entityService';
import { areaService } from '../../../services/areaService';
import { userService, User } from '../../../services/userService';
import { perfilService, Profile } from '../../../services/perfilService';
import { userProfileService, UsuarioPerfilEntidadeRequest, UsuarioAcessoAreaResponse } from '../../../services/userProfileService';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
interface UserEntityProfile {
    userId: string;
    entityId: string;
    role: 'admin' | 'user' | 'manager';
    status?: string; // Status of the user profile entity association
}

const EditUserAccessPage: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [entities, setEntities] = useState<Entity[]>([]);
    const [allActiveEntities, setAllActiveEntities] = useState<Entity[]>([]);
    const [userEntityProfiles, setUserEntityProfiles] = useState<UserEntityProfile[]>([]);
    const [userAreaAccess, setUserAreaAccess] = useState<UsuarioAcessoAreaResponse[]>([]);
    const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
    const [selectedAccess, setSelectedAccess] = useState<{ [key: string]: string[] }>({});
    const [selectedProfile, setSelectedProfile] = useState<string>('');
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [removing, setRemoving] = useState<string | null>(null); // Track which entity is being removed

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const userData = await userService.getUserById(userId);
                setUser(userData);

                // Get all entities that the user has access to (both active and inactive)
                const userEntities = await entityService.getUserEntitiesById(userId);

                const entitiesWithAreas: Entity[] = [];
                for (const entity of userEntities) {
                    try {
                        const areas = await areaService.getAvailableAreas(entity.entidade_id);
                        entitiesWithAreas.push({
                            entidade_id: entity.entidade_id,
                            nome: entity.nome,
                            descricao: entity.descricao,
                            status: entity.status,
                            areas: areas.map(area => ({
                                area_id: area.area_id,
                                nome: area.nome,
                                status: area.status
                            }))
                        });
                    } catch (areaError) {
                        console.error(`Error fetching areas for entity ${entity.entidade_id}:`, areaError);
                        entitiesWithAreas.push({
                            entidade_id: entity.entidade_id,
                            nome: entity.nome,
                            descricao: entity.descricao,
                            status: entity.status,
                            areas: []
                        });
                    }
                }

                setEntities(entitiesWithAreas);

                const allActiveEntitiesResponse = await entityService.getEntities({ status: 'ATIVO' }, 0, 1000);
                const allActiveEntities = allActiveEntitiesResponse.content || [];

                const profilesData = await perfilService.getProfiles();
                setProfiles(profilesData);

                const allEntitiesWithAreas: Entity[] = [];
                for (const entity of allActiveEntities) {
                    try {
                        const areas = await areaService.getAvailableAreas(entity.entidade_id);
                        allEntitiesWithAreas.push({
                            entidade_id: entity.entidade_id,
                            nome: entity.nome,
                            descricao: entity.descricao,
                            status: entity.status,
                            areas: areas.map(area => ({
                                area_id: area.area_id,
                                nome: area.nome,
                                status: area.status
                            }))
                        });
                    } catch (areaError) {
                        console.error(`Error fetching areas for entity ${entity.entidade_id}:`, areaError);
                        allEntitiesWithAreas.push({
                            entidade_id: entity.entidade_id,
                            nome: entity.nome,
                            descricao: entity.descricao,
                            status: entity.status,
                            areas: []
                        });
                    }
                }

                setAllActiveEntities(allEntitiesWithAreas);

                let userEntityProfilesData: UserEntityProfile[] = [];

                try {
                    const userAccessData = await userProfileService.listarAcessoUsuario(userId);

                    userEntityProfilesData = userAccessData.map(access => ({
                        userId: access.usuarioId,
                        entityId: access.entidadeId,
                        role: access.perfilId.toString() as 'admin' | 'user' | 'manager',
                        status: access.status
                    }));

                    setUserEntityProfiles(userEntityProfilesData);

                    // Backend now returns only active profiles, so no filtering needed
                    setEntities(entitiesWithAreas);

                    try {
                        const userAreaAccessData = await userProfileService.listarAcessoAreaUsuario(userId);
                        // Backend now returns only active area access, so no filtering needed
                        setUserAreaAccess(userAreaAccessData);
                    } catch (areaAccessError) {
                        console.error('Error fetching user area access data:', areaAccessError);
                        setUserAreaAccess([]);
                    }

                } catch (accessError) {
                    console.error('Error fetching user access data:', accessError);
                    setUserEntityProfiles([]);
                    setUserAreaAccess([]);
                }

                const initialAccess: { [key: string]: string[] } = {};

                allEntitiesWithAreas.forEach(entity => {
                    initialAccess[entity.entidade_id] = [];
                });

                setSelectedAccess(initialAccess);
                setSelectedProfile('');

            } catch (err) {
                setError('Failed to fetch data. Please try again later.');
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [userId]);

    const handleEntityChange = (entityId: string) => {
        setSelectedEntity(entityId);

        // Set default profile to the first available profile
        if (profiles.length > 0) {
            setSelectedProfile(profiles[0].perfil_id.toString());
        }
    };

    const handleAreaToggle = (entityId: string, areaId: string) => {
        setSelectedAccess(prev => {
            const currentAreas = prev[entityId] || [];
            const newAreas = currentAreas.includes(areaId)
                ? currentAreas.filter(id => id !== areaId)
                : [...currentAreas, areaId];

            return {
                ...prev,
                [entityId]: newAreas
            };
        });
    };

    const handleSelectAllAreas = (entityId: string, checked: boolean) => {
        const entity = allActiveEntities.find(e => e.entidade_id === entityId);
        if (!entity) return;

        setSelectedAccess(prev => ({
            ...prev,
            [entityId]: checked ? entity.areas.map(area => area.area_id) : []
        }));
    };

    const isAllAreasSelected = (entityId: string) => {
        const entity = allActiveEntities.find(e => e.entidade_id === entityId);
        if (!entity) return false;

        const selectedAreas = selectedAccess[entityId] || [];
        return entity.areas.length > 0 && selectedAreas.length === entity.areas.length;
    };

    const handleProfileChange = (role: string) => {
        setSelectedProfile(role);
    };

    const shouldShowAreaSelection = (profileId: string): boolean => {
        if (!profileId) return false;

        const selectedProfileData = profiles.find(p => p.perfil_id.toString() === profileId);
        if (!selectedProfileData) return false;

        // Show area selection only for "Responsável Área" profile
        return selectedProfileData.nome === 'Responsável Área';
    };

    const userHasAccessToEntity = (entityId: string): boolean => {
        return userEntityProfiles.some(
            profile => profile.userId === user?.usuario_id &&
                profile.entityId === entityId
        );
    };

    const handleRemoveAccess = async (entityId: string) => {
        if (!user) return;

        try {
            setRemoving(entityId);
            setError(null);
            setSuccessMessage(null);

            // Find the user profile entity association to get the ID
            const userAccessData = await userProfileService.listarAcessoUsuario(userId);
            const userEntityAccess = userAccessData.find(
                access => access.usuarioId === user.usuario_id && access.entidadeId === entityId
            );

            if (!userEntityAccess) {
                setError('Acesso não encontrado para remoção.');
                return;
            }

            // Remove the user profile entity association
            await userProfileService.removerUsuarioPerfilEntidade(userEntityAccess.usuarioPerfilEntidadeId);

            // Update local state
            setUserEntityProfiles(prev =>
                prev.filter(profile => !(profile.userId === user.usuario_id && profile.entityId === entityId))
            );

            // Remove from entities list
            setEntities(prev => prev.filter(entity => entity.entidade_id !== entityId));

            // Remove area access for this entity
            setUserAreaAccess(prev => prev.filter(area => area.entidadeId !== entityId));

            // Clear selected access for this entity
            setSelectedAccess(prev => {
                const newAccess = { ...prev };
                delete newAccess[entityId];
                return newAccess;
            });

            const entityName = allActiveEntities.find(e => e.entidade_id === entityId)?.nome || 'Entidade';
            setSuccessMessage(`Acesso do usuário à entidade "${entityName}" foi removido com sucesso!`);

        } catch (err) {
            setError('Falha ao remover o acesso. Por favor, tente novamente.');
            console.error('Error removing user access:', err);
        } finally {
            setRemoving(null);
        }
    };

    const handleSubmit = async () => {
        if (!user || !selectedEntity) return;

        try {
            setSaving(true);
            setError(null);
            setSuccessMessage(null);

            // Get the selected profile
            const selectedProfileId = selectedProfile;

            if (!selectedProfileId) {
                setError('Por favor, selecione um perfil para esta entidade.');
                return;
            }

            // Check if user already has access to this entity
            if (userHasAccessToEntity(selectedEntity)) {
                const entityName = allActiveEntities.find(e => e.entidade_id === selectedEntity)?.nome || 'Entidade';
                setError(`O usuário já possui um perfil atribuído à entidade "${entityName}". Para alterar o perfil, primeiro remova o acesso existente e depois adicione o novo perfil.`);
                return;
            }

            // Get the selected profile data to check if it's "Responsável Área"
            const selectedProfileData = profiles.find(p => p.perfil_id.toString() === selectedProfileId);
            const isResponsavelArea = selectedProfileData?.nome === 'Responsável Área';

            // Create the user profile entity request
            const request: UsuarioPerfilEntidadeRequest = {
                usuarioId: userId,
                perfilId: parseInt(selectedProfileId),
                entidadeId: selectedEntity,
                status: 'ATIVO',
                ...(isResponsavelArea && {
                    areaIds: selectedAccess[selectedEntity] || []
                })
            };

            await userProfileService.criarUsuarioPerfilEntidade(request);

            // Note: We'll rely on the API refresh below to update the UI state
            // This prevents double refresh of the "Acessos e Perfis Atuais" card

            // Create a more informative success message
            const entityName = allActiveEntities.find(e => e.entidade_id === selectedEntity)?.nome || 'Entidade';
            const profileName = selectedProfileData?.nome || 'Perfil';
            const areaInfo = isResponsavelArea && selectedAccess[selectedEntity] && selectedAccess[selectedEntity].length > 0
                ? ` com acesso a ${selectedAccess[selectedEntity].length} área(s)`
                : '';

            setSuccessMessage(`Acesso do usuário à entidade "${entityName}" com perfil "${profileName}"${areaInfo} foi salvo com sucesso!`);

            // Refresh user access data after a short delay to ensure server sync
            setTimeout(async () => {
                try {
                    setRefreshing(true);
                    const userAccessData = await userProfileService.listarAcessoUsuario(userId);
                    const updatedUserEntityProfiles = userAccessData.map(access => ({
                        userId: access.usuarioId,
                        entityId: access.entidadeId,
                        role: access.perfilId.toString() as 'admin' | 'user' | 'manager',
                        status: access.status
                    }));
                    setUserEntityProfiles(updatedUserEntityProfiles);

                    // Backend now returns only active profiles, so no filtering needed
                    // Get entities for all returned profiles and ensure they're in the entities list
                    const entityIds = updatedUserEntityProfiles.map(profile => profile.entityId);
                    const entitiesToShow = allActiveEntities.filter(entity =>
                        entityIds.includes(entity.entidade_id)
                    );
                    setEntities(entitiesToShow);

                    const userAreaAccessData = await userProfileService.listarAcessoAreaUsuario(userId);
                    // Backend now returns only active area access, so no filtering needed
                    setUserAreaAccess(userAreaAccessData);
                } catch (refreshError) {
                    console.error('Error refreshing user access data:', refreshError);
                } finally {
                    setRefreshing(false);
                }
            }, 500);

            setSelectedEntity(null);
            setSelectedAccess(prev => ({ ...prev, [selectedEntity]: [] }));

            // Set a default profile for the next entity selection
            if (profiles.length > 0) {
                setSelectedProfile(profiles[0].perfil_id.toString());
            } else {
                setSelectedProfile('');
            }

        } catch (err) {
            setError('Falha ao salvar as alterações. Por favor, tente novamente.');
            console.error('Error saving changes:', err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="text-center p-5">Loading...</div>;
    }

    if (!user) {
        return <div className="text-center p-5">User not found</div>;
    }

    return (
        <Container fluid className="p-4">
            <Button
                variant="outline-secondary"
                onClick={() => history.push('/admin/usuarios')}
                className="return-button mb-3 d-flex align-items-center gap-2"
            >
                <span>←</span> Voltar
            </Button>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Gerenciar Acessos e Perfis - {user.nome} {user.sobrenome}</h2>
            </div>

            {entities.length === 0 && (
                <Alert variant="info" className="mb-4">
                    <div className="d-flex align-items-center">
                        <i className="mdi mdi-account-plus me-2"></i>
                        <div>
                            <strong>Novo Usuário</strong>
                            <br />
                            <small className="text-muted">
                                Este usuário foi criado recentemente e ainda não possui acessos configurados.
                                Use a seção abaixo para configurar seus acessos e perfis.
                            </small>
                        </div>
                    </div>
                </Alert>
            )}

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" onClose={() => setSuccessMessage(null)} dismissible>
                    {successMessage}
                </Alert>
            )}

            {/* Current Access Summary */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Acessos e Perfis Atuais</h5>
                </Card.Header>
                <Card.Body>
                    {loading || refreshing ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="text-muted mt-2">Carregando acessos e perfis...</p>
                        </div>
                    ) : entities.length === 0 ? (
                        <div className="text-center py-4">
                            <p className="text-muted mb-0">Usuário não tem acesso a nenhuma entidade</p>
                            <p className="text-info mt-2 mb-0">
                                <i className="mdi mdi-information-outline me-1"></i>
                                Use a seção abaixo para configurar os acessos e perfis deste usuário
                            </p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table size="sm" bordered hover>
                                <thead>
                                    <tr>
                                        <th>Entidade</th>
                                        <th>Perfil</th>
                                        <th>Áreas com Acesso</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entities.map(entity => {
                                        const profile = userEntityProfiles.find(
                                            p => p.userId === user.usuario_id && p.entityId === entity.entidade_id
                                        );

                                        const entityAreaAccess = userAreaAccess.filter(
                                            area => area.entidadeId === entity.entidade_id
                                        );

                                        const areaNames = entityAreaAccess.map(area => area.areaNome);
                                        const uniqueAreaNames = areaNames.filter((name, index) => areaNames.indexOf(name) === index);
                                        const areasDisplay = uniqueAreaNames.length > 0 ? uniqueAreaNames.join(', ') : 'Nenhuma';

                                        const profileData = profiles.find(p => p.perfil_id.toString() === profile?.role);
                                        const profileName = profileData?.nome || 'Sem perfil';

                                        return (
                                            <tr key={entity.entidade_id}>
                                                <td>{entity.nome}</td>
                                                <td>
                                                    <span className={`badge ${profileData ? 'bg-primary' : 'bg-secondary'}`}>
                                                        {profileName}
                                                    </span>
                                                </td>
                                                <td>
                                                    {areasDisplay === 'Nenhuma' ? (
                                                        <span className="text-muted">Áreas não configuradas</span>
                                                    ) : (
                                                        areasDisplay
                                                    )}
                                                </td>
                                                <td>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleRemoveAccess(entity.entidade_id)}
                                                        disabled={removing === entity.entidade_id || saving || refreshing}
                                                        className="d-flex align-items-center gap-1"
                                                    >
                                                        {removing === entity.entidade_id ? (
                                                            <>
                                                                <div className="spinner-border spinner-border-sm" role="status">
                                                                    <span className="visually-hidden">Removendo...</span>
                                                                </div>
                                                                Removendo...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="mdi mdi-delete"></i>
                                                                Remover
                                                            </>
                                                        )}
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <hr className="my-4" />

            <h6 className="mb-3">Editar Acessos e Perfis</h6>
            <div className="mb-4">
                <Form.Group>
                    <Form.Label>Selecione a Entidade</Form.Label>
                    <Form.Text className="text-muted mb-2 d-block">
                        Selecione uma entidade para adicionar um novo acesso. Entidades onde o usuário já possui um perfil não aparecem nesta lista.
                    </Form.Text>
                    <SearchableDropdown
                        options={allActiveEntities
                            .filter(entity => !userHasAccessToEntity(entity.entidade_id))
                            .map(entity => ({
                                value: entity.entidade_id,
                                label: entity.nome
                            }))}
                        value={selectedEntity || ''}
                        onChange={handleEntityChange}
                        placeholder="Selecione uma entidade..."
                        searchPlaceholder="🔍 Buscar entidade..."
                        disabled={loading || refreshing}
                    />
                    {allActiveEntities.filter(entity => !userHasAccessToEntity(entity.entidade_id)).length === 0 && (
                        <div className="text-muted mt-2">
                            <i className="mdi mdi-information-outline me-1"></i>
                            O usuário já possui um perfil em todas as entidades disponíveis.
                        </div>
                    )}
                </Form.Group>
            </div>

            {selectedEntity && (
                <>
                    <Card className="mb-4">
                        <Card.Header>
                            <h5 className="mb-0">Perfil do Usuário</h5>
                        </Card.Header>
                        <Card.Body>
                            <Form.Group>
                                <Form.Label>Perfil na Entidade</Form.Label>
                                <Form.Text className="text-muted">
                                    Selecione o perfil que este usuário terá nesta entidade.
                                </Form.Text>
                                <Form.Control
                                    as="select"
                                    value={selectedProfile}
                                    onChange={(e) => handleProfileChange(e.target.value)}
                                >
                                    {/* <option value="">Sem perfil</option> */}
                                    {profiles.map(profile => (
                                        <option key={profile.perfil_id} value={profile.perfil_id.toString()}>
                                            {profile.nome}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Card.Body>
                    </Card>

                    {shouldShowAreaSelection(selectedProfile) && (
                        <Card>
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Áreas de Acesso</h5>
                                <Form.Check
                                    type="checkbox"
                                    id={`select-all-${selectedEntity}`}
                                    label="Selecionar Todas as Áreas"
                                    checked={isAllAreasSelected(selectedEntity)}
                                    onChange={(e) => handleSelectAllAreas(selectedEntity, e.target.checked)}
                                    className="mb-0"
                                />
                            </Card.Header>
                            <Card.Body>
                                <div className="row">
                                    {allActiveEntities
                                        .find(e => e.entidade_id === selectedEntity)
                                        ?.areas.map(area => (
                                            <div key={area.area_id} className="col-md-6 mb-3">
                                                <Form.Check
                                                    type="checkbox"
                                                    id={`area-${area.area_id}`}
                                                    label={
                                                        <div>
                                                            <strong>{area.nome}</strong>
                                                            <br />
                                                            <small className="text-muted">
                                                                {area.status}
                                                            </small>
                                                        </div>
                                                    }
                                                    checked={selectedAccess[selectedEntity]?.includes(area.area_id) || false}
                                                    onChange={() => handleAreaToggle(selectedEntity, area.area_id)}
                                                />
                                            </div>
                                        ))}
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                </>
            )}

            {!selectedEntity && (
                <Alert variant="info">
                    Selecione uma entidade para gerenciar os acessos e perfil do usuário.
                </Alert>
            )}

            <div className="mt-4 d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={() => history.push('/admin/usuarios')}>
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={!selectedEntity || !selectedProfile || saving || refreshing}
                >
                    {saving ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
            </div>
        </Container>
    );
};

export default EditUserAccessPage; 